import dynamic from 'next/dynamic'
import { Schemas } from '~/api/gen'
import { MemberBanner } from '~/components/modules/MemberBanner'
import { MemberContentTab } from '~/components/modules/MemberContentTab'
import { FollowedLabel } from '~/components/modules/MemberProfile/FollowedLabel/FollowedLabel'
import { MemberProfile } from '~/components/modules/MemberProfile/MemberProfile'
import { MemberProfileBackground } from '~/components/modules/MemberProfile/MemberProfileBackground'
import { MemberProfilePopup } from '~/components/modules/MemberProfile/MemberProfilePopup'
import { useBinary } from '~/hooks/useBinary'
import { useIsLoggedIn } from '~/hooks/useIsLoggedIn'
import { useIsLoginMember } from '~/hooks/useIsLoginMember'
import { useMainHeaderShadowVisibilityForPcActions } from '~/hooks/useMainHeaderShadowVisibilityForPc'
import { useIsDesktop } from '~/hooks/useMediaQuery'
import { useModal } from '~/hooks/useModal'
import { useRelationship } from '~/hooks/useRelationship'
import { useSubHeaderFloating } from '~/hooks/useSubHeaderFloating'
import { Breadcrumb } from '../../breadcrumbList/components/Breadcrumb'
import { FollowButton } from '../../components/FollowButton'
import { TabButtonGroup } from '../../components/TabButtonGroup'
import { ALERT_TEXT_FOLLOW_NOT_ALLOWED } from '../constants'
import { pcMemberDetailAds } from '../data/googleAd'
import { CollapsableContainer } from './CollapsableContainer'
import { LoginDialog } from './LoginDialog'

type Page = 'coordinates' | 'videos' | 'folders' | 'products'

type Props = {
  pageType: Page
  member: Schemas.member_large_detail
  currentPage?: number
  children: React.ReactNode
}

const ThinBannerForPC = dynamic(
  () => import('~/features/shared/banner/PC/ThinBanner'),
  {
    ssr: false,
  }
)

const GoogleAd = dynamic(
  () => import('~/features/shared/ad/components/GoogleAd'),
  {
    ssr: false,
  }
)

// eslint-disable-next-line complexity
export const MemberDetailLayout: React.FC<Props> = ({
  pageType,
  member,
  currentPage = 1,
  children,
}) => {
  const [isProfileExpanded, , , toggle] = useBinary(false)

  const { followed, following, blocked, follow, unfollow } =
    useRelationship(member)
  const [Modal, openModal, closeModal, isOpen] = useModal(200)
  const isDesktop = useIsDesktop()
  const isLoggedIn = useIsLoggedIn()
  const isLoginMemberContent = useIsLoginMember(member.user_name)
  const { subHeaderRef: pcSubHeaderRef, floating: pcSubHeaderFloating } =
    useSubHeaderFloating()
  const { showPcShadow, hidePcShadow } =
    useMainHeaderShadowVisibilityForPcActions()
  if (pcSubHeaderFloating) {
    hidePcShadow()
  } else {
    showPcShadow()
  }

  const isPostContent = pageType === 'coordinates' || pageType === 'videos'
  const isFirstPage = currentPage === 1

  const postTypeItems = [
    {
      key: 'coordinates',
      title: 'コーディネート',
      href: `/${member.user_name}/`,
    },
    {
      key: 'videos',
      title: 'ノウハウ動画',
      href: `/${member.user_name}/videos/`,
    },
  ]

  return (
    <>
      {isFirstPage ? (
        <MemberProfileBackground
          backgroundImage={member.background_image.url_640 ?? undefined}
          isExpanded={isProfileExpanded}
        >
          <div className="hidden xl:block">
            <ThinBannerForPC bannerType="pc_gbl_ad_user_detail" />
          </div>
          <div className="mx-auto pb-5 xl:w-[990px] xl:pb-[30px] xl:pt-[15px]">
            <div className="xl:flex xl:justify-between">
              <div className="hidden xl:block xl:pt-[7px]">
                <Breadcrumb />
              </div>
              <div className="absolute right-2 top-[5px] xl:static">
                <MemberProfilePopup
                  member={member}
                  appUrl={`wear2020://wear.jp/user/${member.id}/`}
                  showBlockAndReportButton={isLoggedIn && !isLoginMemberContent}
                />
              </div>
            </div>
            {followed && (
              <p className="absolute left-[15px] top-5 xl:hidden">
                <FollowedLabel />
              </p>
            )}
            <div className="pt-8 xl:pt-[22px]">
              <MemberProfile
                member={member}
                isExpanded={isProfileExpanded}
                isFollowed={followed}
                isMemberImageLargestContent={pageType === 'folders'}
                toggle={toggle}
                actionButton={
                  <div className="h-9 w-[168px] text-[13px] xl:w-[136px] xl:text-[12px]">
                    <FollowButton
                      follow={() => {
                        if (blocked) {
                          alert(ALERT_TEXT_FOLLOW_NOT_ALLOWED)
                          return
                        }
                        if (isLoggedIn) follow()
                        else openModal()
                      }}
                      unfollow={unfollow}
                      isFollowing={following}
                    />
                  </div>
                }
              />
            </div>
          </div>
        </MemberProfileBackground>
      ) : (
        <>
          <div className="hidden xl:block">
            <ThinBannerForPC bannerType="pc_gbl_ad_user_detail" />
          </div>
          <div className="hidden xl:block xl:bg-white">
            <div className="xl:mx-auto xl:flex xl:w-[990px] xl:justify-between xl:py-[15px]">
              <div className="xl:pt-[7px]">
                <Breadcrumb />
              </div>
              <div className="absolute right-2 top-[5px] xl:static xl:z-shareModal">
                <MemberProfilePopup member={member} />
              </div>
            </div>
          </div>
        </>
      )}
      <div
        className={`xl:sticky xl:top-[54px] xl:z-contentHeader ${isFirstPage ? 'hidden xl:block' : ''}`}
        ref={pcSubHeaderRef}
      >
        <div
          aria-hidden={!pcSubHeaderFloating}
          className={
            isFirstPage
              ? 'xl:visible xl:absolute xl:w-screen xl:translate-y-0 xl:opacity-100 xl:transition-[opacity,transform] xl:duration-150 xl:ease-out aria-hidden:xl:invisible aria-hidden:xl:translate-y-[-15px] aria-hidden:xl:opacity-0'
              : ''
          }
        >
          <MemberBanner
            member={member}
            href={`/${member.user_name}/`}
            linkLabel={`${member.nick_name}のページへ`}
            hasShadow={pcSubHeaderFloating}
            isFollowed={followed}
            actionButton={
              <div className="h-8 w-[100px] text-[12px] xl:w-[134px]">
                <FollowButton
                  follow={() => {
                    if (blocked) {
                      alert(ALERT_TEXT_FOLLOW_NOT_ALLOWED)
                      return
                    }
                    if (isLoggedIn) follow()
                    else openModal()
                  }}
                  unfollow={unfollow}
                  isFollowing={following}
                />
              </div>
            }
          />
        </div>
      </div>
      <Modal
        wrapperClassName={`ease-linear duration-200 transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        overlayClassName="bg-black/50 xl:bg-black/60"
        className={`transition duration-100 ease-linear xl:transition-none ${
          isOpen ? 'scale-100 opacity-100 delay-100' : 'scale-90 opacity-0'
        }`}
      >
        <LoginDialog
          onCloseClick={closeModal}
          text={'フォローは\nログインまたは会員登録するとできます'}
        />
      </Modal>
      <CollapsableContainer
        className="relative z-10 bg-white xl:-mb-10 xl:bg-gray-50 xl:pb-10"
        collapsed={isProfileExpanded && !isDesktop}
      >
        {isFirstPage && (
          <MemberContentTab
            userName={member.user_name}
            activeTab={isPostContent ? 'posts' : pageType}
          />
        )}
        <div className="xl:mx-auto xl:w-[990px]">
          {isPostContent && isFirstPage && (
            <div className="px-4 py-[18px] xl:px-0 xl:py-6">
              <TabButtonGroup items={postTypeItems} activeTab={pageType} />
            </div>
          )}
          {children}
          {isPostContent && (
            <div className="hidden justify-center gap-x-[46px] pt-[30px] xl:flex">
              {pcMemberDetailAds.map((ad) => (
                <GoogleAd key={ad} elementId={ad} />
              ))}
            </div>
          )}
        </div>
      </CollapsableContainer>
    </>
  )
}
